<template>
  <v-container fluid class="erp-page home">
    <section class="erp-page-header hero">
      <div>
        <h1 class="erp-page-title">Panel principal</h1>
        <p>Vista visual de mostrador, precios y stock crítico.</p>
      </div>
      <div class="hero-actions">
        <v-btn text small color="primary" class="hero-refresh-btn" :loading="loading" @click="loadWidgets">Recargar</v-btn>
        <v-menu offset-y :close-on-content-click="false">
          <template v-slot:activator="{ on, attrs }">
            <v-btn small color="primary" class="hero-widget-btn" v-bind="attrs" v-on="on">Widgets</v-btn>
          </template>
          <v-card class="widget-menu-card">
            <v-card-title class="subtitle-2">Widgets visibles</v-card-title>
            <v-card-text>
              <v-checkbox
                v-for="item in availableWidgets"
                :key="item.id"
                v-model="selectedWidgetIds"
                :value="item.id"
                :label="item.label"
                dense
                hide-details
                class="mb-1"
              />
            </v-card-text>
          </v-card>
        </v-menu>
      </div>
    </section>

    <v-alert v-if="error" type="warning" dense outlined class="mt-3">
      {{ error }}
    </v-alert>

    <v-row class="mt-1" dense>
      <v-col 
        v-for="(widgetId, index) in orderedWidgetIds" 
        :key="widgetId" 
        cols="12" 
        :md="widgetCols(widgetId)" 
        :data-widget-id="widgetId"
        class="widget-col"
        :class="{ 'widget-dragging': draggingWidget === widgetId, 'widget-drop-target': dropTarget === widgetId }"
        draggable="true"
        @dragstart="onDragStart($event, widgetId, index)"
        @dragover.prevent="onDragOver($event, widgetId, index)"
        @drop="onDrop($event, widgetId, index)"
        @dragend="onDragEnd"
      >
        <v-card 
          outlined 
          :class="['widget-card', `widget-card--${widgetId}`, { 'widget-resizing': resizingWidget === widgetId }]"
          :style="getWidgetStyle(widgetId)"
        >
          <div class="widget-top" :class="{ 'widget-drag-handle': true }">
            <div class="widget-drag-icon" title="Arrastrar para mover">
              <v-icon x-small>mdi-drag-horizontal</v-icon>
            </div>
            <h3 :class="['widget-title', `widget-title--${widgetId}`]">{{ widgetLabel(widgetId) }}</h3>
            <div class="widget-actions">
              <v-btn icon small class="widget-icon-btn" :disabled="index === 0" @click="moveWidget(widgetId, -1)">
                <v-icon small>mdi-chevron-up</v-icon>
              </v-btn>
              <v-btn icon small class="widget-icon-btn" :disabled="index === orderedWidgetIds.length - 1" @click="moveWidget(widgetId, 1)">
                <v-icon small>mdi-chevron-down</v-icon>
              </v-btn>
            </div>
          </div>
          <div 
            class="widget-resize-handle" 
            title="Arrastra para redimensionar"
            @mousedown="startResize($event, widgetId)"
            @touchstart="startResize($event, widgetId)"
          >
            <v-icon x-small>mdi-resize-bottom-right</v-icon>
          </div>

          <v-card-text v-if="widgetId === 'topCounterQueries'" class="widget-section widget-section--queries">
            <div class="metrics-row">
              <div class="metric-tile">
                <span>Consultas</span>
                <strong>{{ formatInt(topQueries.totalSearches) }}</strong>
              </div>
              <div class="metric-tile">
                <span>Ventana</span>
                <strong>{{ topQueries.periodDays }} días</strong>
              </div>
            </div>
            <div class="bars mt-2">
              <div v-for="row in topQueries.rows.slice(0, 8)" :key="row.key || row.productId || row.code" class="bar-row">
                <div class="bar-label">{{ row.code || row.query }}</div>
                <div class="bar-track">
                  <div class="bar-fill" :style="{ width: `${topQueryWidth(row.queries)}%` }"></div>
                </div>
                <div class="bar-value">{{ formatInt(row.queries) }}</div>
              </div>
              <div v-if="!topQueries.rows.length" class="empty-cell">Sin datos de consultas.</div>
            </div>
          </v-card-text>

          <v-card-text v-else-if="widgetId === 'priceChangesToday'" class="widget-section">
            <div class="metrics-row">
              <div class="metric-tile">
                <span>Cambios semana</span>
                <strong>{{ formatInt(priceChanges.totalChanges) }}</strong>
              </div>
              <div class="metric-tile">
                <span>Promedio abs</span>
                <strong>{{ formatPercent(priceChanges.avgChangePercent) }}</strong>
              </div>
            </div>
            <div v-if="priceChanges.totalChanges > 0" class="visual-row mt-4">
              <div class="chart-cell chart-cell--donut">
                <apexchart
                  type="donut"
                  height="160"
                  :options="chartOptionsDonutPrice"
                  :series="chartSeriesDonutPrice"
                />
              </div>
              <div class="chart-cell chart-cell--line">
                <apexchart
                  type="area"
                  height="160"
                  :options="chartOptionsLineTrend"
                  :series="chartSeriesLineTrend"
                />
              </div>
            </div>
          </v-card-text>

          <v-card-text v-else-if="widgetId === 'sellerRanking'" class="widget-section">
            <div class="metrics-row">
              <div class="metric-tile">
                <span>Vendedor Top</span>
                <strong>{{ sellerRanking.rows[0] ? sellerRanking.rows[0].sellerName : '—' }}</strong>
              </div>
              <div class="metric-tile">
                <span>Total Presupuestos</span>
                <strong>{{ formatInt(totalBudgets) }}</strong>
              </div>
            </div>
            <div class="bars mt-2">
              <div v-for="row in sellerRanking.rows" :key="row.sellerName" class="bar-row">
                <div class="bar-label">{{ row.sellerName }}</div>
                <div class="bar-track">
                  <div class="bar-fill" :style="{ width: `${sellerRankingWidth(row.budgetCount)}%` }"></div>
                </div>
                <div class="bar-value">{{ formatInt(row.budgetCount) }}</div>
              </div>
              <div v-if="!sellerRanking.rows.length" class="empty-cell">Sin actividad reciente.</div>
            </div>
          </v-card-text>

          <v-card-text v-else-if="widgetId === 'dailyFacturation'" class="widget-section widget-section--facturation">
            <div class="facturation-compact">
              <div class="facturation-data">
                <div class="facturation-header">
                  <v-icon size="16" color="#166534">mdi-cash-register</v-icon>
                  <span>Facturado hoy</span>
                </div>
                <div class="facturation-row">
                  <div class="facturation-amount">$ {{ formatNumber(dailyFacturation.totalToday) }}</div>
                  <div class="facturation-meta">{{ dailyFacturation.salesCount }} ventas • {{ todayDate }}</div>
                </div>
              </div>
              <v-btn
                icon
                small
                color="success"
                class="facturation-link"
                title="Ver historial"
                :to="{ name: 'sales-counter-invoices' }"
              >
                <v-icon small>mdi-file-document-outline</v-icon>
              </v-btn>
            </div>
            <div v-if="weeklyTrend.rows && weeklyTrend.rows.length && dailyFacturation.totalToday > 0" class="facturation-chart-mini">
              <apexchart
                type="area"
                height="50"
                :options="chartOptionsWeeklySales"
                :series="chartSeriesWeeklySales"
              />
            </div>
          </v-card-text>

          <v-card-text v-else-if="widgetId === 'criticalStock'" class="widget-section">
            <div class="caption mb-2">
              Crítico = stock disponible en cero o por debajo del mínimo configurado.
            </div>
            <div class="metrics-row">
              <div class="metric-tile danger">
                <span>Críticos</span>
                <strong>{{ formatInt(criticalStock.totalCritical) }}</strong>
              </div>
              <div class="metric-tile">
                <span>En cero</span>
                <strong>{{ formatInt(criticalStock.totalZero) }}</strong>
              </div>
              <div class="metric-tile">
                <span>Bajo mínimo</span>
                <strong>{{ formatInt(criticalStock.totalBelowMinimum) }}</strong>
              </div>
            </div>
            <div v-if="priceChanges.totalChanges > 0" class="visual-row mt-4">
              <div class="chart-cell chart-cell--donut">
                <apexchart
                  type="donut"
                  height="160"
                  :options="chartOptionsDonutStock"
                  :series="chartSeriesDonutStock"
                />
              </div>
              <div class="bars-wrap">
                <div v-for="row in criticalStock.rows.slice(0, 6)" :key="row.productId" class="critical-row">
                  <div class="critical-info">
                    <span class="critical-code">{{ row.code }}</span>
                    <span class="critical-stock">Disp: {{ formatInt(row.availableTotal) }} | Min: {{ formatInt(row.minimumTotal) }}</span>
                  </div>
                  <div class="critical-bar-track">
                    <div class="critical-bar-fill" :style="{ width: `${stockDeficitWidth(row)}%` }"></div>
                  </div>
                </div>
                <div v-if="!criticalStock.rows.length" class="empty-cell">Sin stock crítico.</div>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { dashboardService } from "../services/dashboard";
import { pricingStockService } from "../services/pricingStock";
import { stockService } from "../services/stock";

const STORAGE_KEY = "herra.home.widgets.v2";
const WIDGET_ORDER = ["dailyFacturation", "criticalStock", "sellerRanking", "topCounterQueries", "priceChangesToday"];
const WEEK_DAYS = 7;
const TREND_WEEKS = 8;

const WIDGET_PERMISSIONS = {
  sellerRanking: "widget:sellerRanking:view",
  dailyFacturation: "widget:dailyFacturation:view",
  topCounterQueries: "widget:topCounterQueries:view",
  priceChangesToday: "widget:priceChangesToday:view",
  criticalStock: "widget:criticalStock:view"
};

export default {
  name: "Home",
  data() {
    return {
      loading: false,
      selectedWidgetIds: ["dailyFacturation", "criticalStock", "sellerRanking", "topCounterQueries", "priceChangesToday"],
      widgetSizes: {
        sellerRanking: "medium",
        dailyFacturation: "medium",
        topCounterQueries: "medium",
        priceChangesToday: "medium",
        criticalStock: "medium",
      },
      widgetWidths: {},
      widgetHeights: {},
      resizingWidget: null,
      resizeStartX: 0,
      resizeStartY: 0,
      resizeStartWidth: 0,
      resizeStartHeight: 0,
      draggingWidget: null,
      dragSourceIndex: null,
      dropTarget: null,
      topQueries: { periodDays: WEEK_DAYS, totalSearches: 0, rows: [] },
      priceChanges: { totalChanges: 0, avgChangePercent: null, upCount: 0, downCount: 0, rows: [] },
      priceTrend: [],
      criticalStock: { totalCritical: 0, totalZero: 0, totalBelowMinimum: 0, rows: [] },
      sellerRanking: { rows: [] },
      dailyFacturation: { totalToday: 0, salesCount: 0 },
      weeklyTrend: { rows: [] },
    };
  },
  computed: {
    userPermissions() {
      const user = this.$store.state.auth?.user;
      return Array.isArray(user?.permissions) ? user.permissions : [];
    },
    availableWidgets() {
      const staticWidgets = [
        { id: "sellerRanking", label: "Ranking de vendedores (30d)" },
        { id: "dailyFacturation", label: "Caja facturada hoy" },
        { id: "topCounterQueries", label: "Top productos consultados en mostrador" },
        { id: "priceChangesToday", label: "Cambios de precio semanal" },
        { id: "criticalStock", label: "Stock crítico" },
      ];
      return staticWidgets.filter(w => {
        const perm = WIDGET_PERMISSIONS[w.id];
        if (!perm) return true;
        return this.userPermissions.includes(perm);
      });
    },
    orderedWidgetIds() {
      const allowedIds = this.availableWidgets.map(w => w.id);
      const selected = this.selectedWidgetIds.filter((id) => allowedIds.includes(id));
      return WIDGET_ORDER.filter((id) => selected.includes(id));
    },
    totalBudgets() {
      return this.sellerRanking.rows.reduce((sum, r) => sum + r.budgetCount, 0);
    },
    isDark() {
      return document.documentElement.getAttribute("data-theme") === "dark";
    },
    todayDate() {
      const d = new Date();
      return d.toLocaleDateString("es-AR", { day: "numeric", month: "short" });
    },
    // ApexCharts Configurations
    chartOptionsDonutPrice() {
      return {
        chart: { type: "donut", fontFamily: "inherit" },
        labels: ["Subas", "Bajas", "Sin cambios"],
        colors: ["#10b981", "#ef4444", "#cbd5e1"],
        stroke: { show: false },
        dataLabels: { enabled: false },
        legend: { position: "bottom", labels: { colors: this.isDark ? "#94a3b8" : "#475569" } },
        plotOptions: {
          pie: {
            donut: {
              size: "75%",
              labels: {
                show: true,
                total: {
                  show: true,
                  label: "Total",
                  formatter: () => this.formatInt(this.priceChanges.totalChanges),
                  color: this.isDark ? "#f1f5f9" : "#1e293b"
                }
              }
            }
          }
        }
      };
    },
    chartSeriesDonutPrice() {
      return [this.priceChanges.upCount, this.priceChanges.downCount, Math.max(0, this.priceChanges.totalChanges - this.priceChanges.upCount - this.priceChanges.downCount)];
    },
    chartOptionsDonutStock() {
      return {
        chart: { type: "donut", fontFamily: "inherit" },
        labels: ["En cero", "Bajo mín"],
        colors: ["#dc2626", "#f59e0b"],
        stroke: { show: false },
        dataLabels: { enabled: false },
        legend: { position: "bottom", labels: { colors: this.isDark ? "#94a3b8" : "#475569" } },
        plotOptions: {
          pie: {
            donut: {
              size: "75%",
              labels: {
                show: true,
                total: {
                  show: true,
                  label: "Críticos",
                  formatter: () => this.formatInt(this.criticalStock.totalCritical),
                  color: this.isDark ? "#f1f5f9" : "#1e293b"
                }
              }
            }
          }
        }
      };
    },
    chartSeriesDonutStock() {
      return [this.criticalStock.totalZero, this.criticalStock.totalBelowMinimum];
    },
    chartOptionsLineTrend() {
      return {
        chart: {
          type: "area",
          toolbar: { show: false },
          sparkline: { enabled: false },
          fontFamily: "inherit"
        },
        dataLabels: { enabled: false },
        stroke: { curve: "smooth", width: 3 },
        fill: {
          type: "gradient",
          gradient: { shadeIntensity: 1, opacityFrom: 0.45, opacityTo: 0.05, stops: [20, 100] }
        },
        xaxis: {
          categories: this.priceTrend.map(r => r.bucket.split("-W")[1]),
          labels: { style: { colors: this.isDark ? "#94a3b8" : "#475569" } },
          axisBorder: { show: false },
          axisTicks: { show: false }
        },
        yaxis: { show: false },
        grid: { show: false },
        colors: ["#3b82f6"],
        tooltip: { theme: this.isDark ? "dark" : "light" }
      };
    },
    chartSeriesLineTrend() {
      return [{ name: "Cambios", data: this.priceTrend.map(r => r.changes) }];
    },
    chartOptionsWeeklySales() {
      return {
        chart: { type: "area", toolbar: { show: false }, sparkline: { enabled: false }, fontFamily: "inherit" },
        dataLabels: { enabled: false },
        stroke: { curve: "smooth", width: 3 },
        fill: {
          type: "gradient",
          gradient: { shadeIntensity: 1, opacityFrom: 0.45, opacityTo: 0.05, stops: [20, 100] }
        },
        xaxis: {
          categories: this.weeklyTrend.rows.map(r => {
            const d = new Date(r.date);
            return d.toLocaleDateString("es-AR", { day: "numeric", month: "short" });
          }),
          labels: { style: { colors: this.isDark ? "#94a3b8" : "#475569" } },
          axisBorder: { show: false },
          axisTicks: { show: false }
        },
        yaxis: { show: false },
        grid: { show: false },
        colors: ["#10b981"],
        tooltip: { theme: this.isDark ? "dark" : "light", y: { formatter: (v) => `$ ${this.formatNumber(v)}` } }
      };
    },
    chartSeriesWeeklySales() {
      return [{ name: "Ventas", data: this.weeklyTrend.rows.map(r => r.total) }];
    }
  },
  watch: {
    selectedWidgetIds: {
      deep: true,
      handler() { this.persistLayout(); },
    },
    widgetSizes: {
      deep: true,
      handler() { this.persistLayout(); },
    },
  },
  created() {
    this.restoreLayout();
    this.loadWidgets();
  },
  methods: {
    unwrap(response) {
      if (response && response.data && Object.prototype.hasOwnProperty.call(response.data, "data")) return response.data.data;
      return response && response.data ? response.data : null;
    },
    widgetLabel(id) {
      const found = this.availableWidgets.find((w) => w.id === id);
      return found ? found.label : id;
    },
    widgetSize(id) {
      return this.widgetSizes[id] || "medium";
    },
    widgetCols(id) {
      const size = this.widgetSize(id);
      if (size === "tiny") return 2;    // 16.67% - mínimo
      if (size === "compact") return 3;  // 25% - ultra compacto
      if (size === "small") return 4;   // 33% - pequeño
      if (id === "dailyFacturation") return 4;
      if (id === "criticalStock") return 8;
      if (id === "priceChangesToday") return 12;
      if (size === "large") return 12;  // 100% - ancho completo
      return 6;                         // 50% - medio (default)
    },
    resizeWidget(id, delta) {
      const scale = ["tiny", "compact", "small", "medium", "large"];
      const current = this.widgetSize(id);
      const idx = scale.indexOf(current);
      if (idx === -1) {
        // Si no está en la escala (ej: medium default), empezar desde small o compact
        const startIdx = delta < 0 ? 2 : 2; // compact por defecto
        this.$set(this.widgetSizes, id, scale[startIdx + delta]);
        return;
      }
      const next = Math.max(0, Math.min(scale.length - 1, idx + delta));
      this.$set(this.widgetSizes, id, scale[next]);
    },
    getWidgetStyle(id) {
      const width = this.widgetWidths[id];
      const height = this.widgetHeights[id];
      const style = {};
      // La grilla usa columnas responsivas; no conservar píxeles del tablero antiguo.
      if (false && width) {
        style.flex = `0 0 ${width}px`;
        style.maxWidth = '100%';
      }
      if (false && height) {
        style.height = `${height}px`;
        style.minHeight = `${height}px`;
      }
      return style;
    },
    startResize(event, id) {
      event.preventDefault();
      event.stopPropagation();
      this.resizingWidget = id;
      
      const clientX = event.touches ? event.touches[0].clientX : event.clientX;
      const clientY = event.touches ? event.touches[0].clientY : event.clientY;
      this.resizeStartX = clientX;
      this.resizeStartY = clientY;
      
      const col = this.$el.querySelector(`[data-widget-id="${id}"]`);
      if (col) {
        this.resizeStartWidth = col.offsetWidth;
        this.resizeStartHeight = col.offsetHeight;
      }
      
      document.addEventListener('mousemove', this.onResize);
      document.addEventListener('mouseup', this.stopResize);
      document.addEventListener('touchmove', this.onResize, { passive: false });
      document.addEventListener('touchend', this.stopResize);
    },
    onResize(event) {
      if (!this.resizingWidget) return;
      event.preventDefault();
      
      const clientX = event.touches ? event.touches[0].clientX : event.clientX;
      const clientY = event.touches ? event.touches[0].clientY : event.clientY;
      const deltaX = clientX - this.resizeStartX;
      const deltaY = clientY - this.resizeStartY;
      
      // Resize horizontal (ancho)
      const newWidth = Math.max(120, this.resizeStartWidth + deltaX);
      this.$set(this.widgetWidths, this.resizingWidget, newWidth);
      
      // Resize vertical (altura) - mínimo 100px
      const newHeight = Math.max(100, this.resizeStartHeight + deltaY);
      this.$set(this.widgetHeights, this.resizingWidget, newHeight);
      
      // Actualizar tamaño lógico basado en ancho
      const containerWidth = this.$el.offsetWidth;
      const colSpan = Math.round((newWidth / containerWidth) * 12);
      
      let newSize = 'medium';
      if (colSpan <= 2) newSize = 'tiny';      // 2 cols = 16.67%
      else if (colSpan <= 3) newSize = 'compact';  // 3 cols = 25%
      else if (colSpan <= 4) newSize = 'small';    // 4 cols = 33%
      else if (colSpan >= 10) newSize = 'large';   // 10-12 cols
      
      this.$set(this.widgetSizes, this.resizingWidget, newSize);
    },
    stopResize() {
      this.resizingWidget = null;
      document.removeEventListener('mousemove', this.onResize);
      document.removeEventListener('mouseup', this.stopResize);
      document.removeEventListener('touchmove', this.onResize);
      document.removeEventListener('touchend', this.stopResize);
      this.persistLayout();
    },
    // Drag & Drop para reordenar widgets
    onDragStart(event, widgetId, index) {
      this.draggingWidget = widgetId;
      this.dragSourceIndex = index;
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', widgetId);
      // Hacer el elemento semi-transparente durante el drag
      setTimeout(() => {
        const col = this.$el.querySelector(`[data-widget-id="${widgetId}"]`);
        if (col) col.style.opacity = '0.4';
      }, 0);
    },
    onDragOver(event, widgetId, index) {
      event.preventDefault();
      if (this.draggingWidget && this.draggingWidget !== widgetId) {
        this.dropTarget = widgetId;
      }
    },
    onDrop(event, widgetId, targetIndex) {
      event.preventDefault();
      this.dropTarget = null;
      
      const sourceId = this.draggingWidget;
      const sourceIndex = this.dragSourceIndex;
      
      if (!sourceId || sourceIndex === null || sourceIndex === targetIndex) {
        this.onDragEnd();
        return;
      }
      
      // Reordenar el array WIDGET_ORDER
      const order = [...WIDGET_ORDER];
      const [moved] = order.splice(sourceIndex, 1);
      order.splice(targetIndex, 0, moved);
      
      WIDGET_ORDER.splice(0, WIDGET_ORDER.length, ...order);
      this.selectedWidgetIds = [...this.selectedWidgetIds];
      this.persistLayout();
      this.onDragEnd();
    },
    onDragEnd() {
      // Restaurar opacidad
      if (this.draggingWidget) {
        const col = this.$el.querySelector(`[data-widget-id="${this.draggingWidget}"]`);
        if (col) col.style.opacity = '';
      }
      this.draggingWidget = null;
      this.dragSourceIndex = null;
      this.dropTarget = null;
    },
    moveWidget(id, direction) {
      const order = [...WIDGET_ORDER];
      const currentIdx = order.indexOf(id);
      const targetIdx = currentIdx + direction;
      if (currentIdx < 0 || targetIdx < 0 || targetIdx >= order.length) return;
      const tmp = order[currentIdx];
      order[currentIdx] = order[targetIdx];
      order[targetIdx] = tmp;
      WIDGET_ORDER.splice(0, WIDGET_ORDER.length, ...order);
      this.selectedWidgetIds = [...this.selectedWidgetIds];
    },
    persistLayout() {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          selectedWidgetIds: this.selectedWidgetIds,
          widgetSizes: this.widgetSizes,
          widgetWidths: this.widgetWidths,
          widgetHeights: this.widgetHeights,
          order: [...WIDGET_ORDER],
        })
      );
    },
    restoreLayout() {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      try {
        const saved = JSON.parse(raw);
        if (Array.isArray(saved.selectedWidgetIds)) {
          this.selectedWidgetIds = saved.selectedWidgetIds.filter((id) => WIDGET_ORDER.includes(id));
        }
        if (saved.widgetSizes && typeof saved.widgetSizes === "object") {
          this.widgetSizes = { ...this.widgetSizes, ...saved.widgetSizes };
        }
        if (saved.widgetWidths && typeof saved.widgetWidths === "object") {
          this.widgetWidths = { ...this.widgetWidths, ...saved.widgetWidths };
        }
        if (saved.widgetHeights && typeof saved.widgetHeights === "object") {
          this.widgetHeights = { ...this.widgetHeights, ...saved.widgetHeights };
        }
        if (Array.isArray(saved.order) && saved.order.length === WIDGET_ORDER.length) {
          const clean = saved.order.filter((id) => WIDGET_ORDER.includes(id));
          if (clean.length === WIDGET_ORDER.length) WIDGET_ORDER.splice(0, WIDGET_ORDER.length, ...clean);
        }
      } catch (_error) {
        // ignore
      }
    },
    async loadWidgets() {
      this.loading = true;
      this.error = "";
      try {
        const response = await dashboardService.getWidgetsOverview({
          topQueriesDays: WEEK_DAYS,
          priceChangesDays: WEEK_DAYS,
          trendGroupBy: "week",
        });
        const data = this.unwrap(response) || {};
        console.log("Dashboard Sync:", data);
        
        // Asignación directa y limpia
        this.topQueries = this.normalizeTopQueries(data.topCounterQueries);
        this.priceChanges = this.normalizePriceChanges(data.priceChangesToday || {});
        this.criticalStock = data.criticalStock || { totalCritical: 0, rows: [] };
        this.sellerRanking = data.sellerRanking || { rows: [] };
        this.dailyFacturation = data.dailyFacturation || { totalToday: 0, salesCount: 0 };
        this.weeklyTrend = data.weeklyTrend || { rows: [] };
        this.priceTrend = data.priceTrend || [];

        // Forzamos que Vue vea el cambio en todo el objeto
        this.$forceUpdate();
        
        await this.loadWeeklyCommercialFallback();
      } catch (_error) {
        await this.loadFallbackWidgets();
        this.error = "Usando datos locales. El backend no soporta la API unificada todavía.";
      } finally {
        this.loading = false;
      }
    },
    normalizeTopQueries(source) {
      const rows = Array.isArray(source && source.rows) ? source.rows : [];
      return {
        periodDays: WEEK_DAYS,
        totalSearches: Number((source && source.totalSearches) || rows.reduce((acc, row) => acc + Number(row.queries || 0), 0) || 0),
        rows,
      };
    },
    normalizePriceChanges(source) {
      const rows = Array.isArray(source.rows) ? source.rows : [];
      const provided = Number(source.avgChangePercent);
      const fromRows = this.computeAverageAbsPercent(rows);
      const avgChangePercent = Number.isFinite(provided) && provided > 0 ? provided : fromRows;
      return {
        totalChanges: Number(source.totalChanges || rows.length || 0),
        avgChangePercent: Number.isFinite(avgChangePercent) ? avgChangePercent : null,
        upCount: Number(source.upCount || 0),
        downCount: Number(source.downCount || 0),
        rows,
      };
    },
    computeAverageAbsPercent(rows) {
      const list = (Array.isArray(rows) ? rows : [])
        .map((row) => Number(row.changePercent))
        .filter((n) => Number.isFinite(n));
      if (!list.length) return null;
      const sum = list.reduce((acc, n) => acc + Math.abs(n), 0);
      return Number((sum / list.length).toFixed(2));
    },
    buildTrendFromRows(rows, days = 14, groupBy = "day") {
      if (groupBy === "week") {
        const now = new Date();
        const weekKeys = [];
        for (let i = TREND_WEEKS - 1; i >= 0; i -= 1) {
          const d = new Date(now);
          d.setDate(now.getDate() - i * 7);
          weekKeys.push(this.weekBucket(d));
        }
        const map = new Map(weekKeys.map((k) => [k, 0]));
        (Array.isArray(rows) ? rows : []).forEach((row) => {
          const raw = row && (row.changedAt || row.bucket) ? String(row.changedAt || row.bucket) : "";
          if (!raw) return;
          const bucket = raw.includes("W") ? raw : this.weekBucket(new Date(raw));
          if (!map.has(bucket)) return;
          map.set(bucket, Number(map.get(bucket) || 0) + Number(row.changes || 1));
        });
        return Array.from(map.entries()).map(([bucket, changes]) => ({ bucket, changes }));
      }
      return [];
    },
    weekBucket(dateValue) {
      const d = new Date(dateValue);
      if (Number.isNaN(d.getTime())) return "";
      const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
      const dayNum = date.getUTCDay() || 7;
      date.setUTCDate(date.getUTCDate() + 4 - dayNum);
      const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
      const weekNo = Math.ceil((((date - yearStart) / 86400000) + 1) / 7);
      return `${date.getUTCFullYear()}-W${String(weekNo).padStart(2, "0")}`;
    },
    async loadWeeklyCommercialFallback() {
      // Implementación simplificada para mantener brevedad
      return;
    },
    async loadFallbackWidgets() {
      // Implementación simplificada para mantener brevedad
      return;
    },
    topQueryWidth(value) {
      const max = Math.max(...(this.topQueries.rows || []).map((r) => Number(r.queries || 0)), 1);
      return (Number(value || 0) / max) * 100;
    },
    stockDeficitWidth(row) {
      const deficit = this.stockDeficit(row);
      const maxDeficit = Math.max(...(this.criticalStock.rows || []).map((r) => this.stockDeficit(r)), 10);
      return (deficit / maxDeficit) * 100;
    },
    stockDeficit(row) {
      return Math.max(Number(row.minimumTotal || 0) - Number(row.availableTotal || 0), 0);
    },
    formatInt(value) {
      const n = Number(value || 0);
      return Number.isFinite(n) ? n.toLocaleString("es-AR") : "0";
    },
    formatPercent(value) {
      const n = Number(value);
      if (!Number.isFinite(n)) return "—";
      return `${n.toFixed(1)}%`;
    },
    formatNumber(value) {
      const n = Number(value || 0);
      return n.toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    sellerRankingWidth(value) {
      const max = Math.max(...this.sellerRanking.rows.map((r) => r.budgetCount), 1);
      return (value / max) * 100;
    },
  },
};
</script>

<style scoped>
.home {
/* Rediseño operativo: el tablero prioriza lectura y acciones, no edición técnica. */
.widget-drag-icon, .widget-actions, .widget-resize-handle { display: none !important; }
.widget-col { cursor: default !important; }
.widget-card { min-height: 238px; border-radius: 14px !important; overflow: hidden; }
.widget-top { min-height: 48px; padding: 0 18px; border-bottom: 1px solid var(--border-subtle); }
.widget-title { font-size: 13px !important; font-weight: 700 !important; color: var(--text-primary) !important; }
.widget-section { padding: 18px !important; }
.widget-card--dailyFacturation { background: linear-gradient(135deg, #effcf5, #ffffff) !important; }
.widget-card--criticalStock { background: linear-gradient(135deg, #fff5f5, #ffffff) !important; }
.widget-card--criticalStock { min-height: 320px; }
.metric-tile { border-radius: 10px !important; min-height: 74px; justify-content: center; }
.metric-tile strong { font-size: 22px !important; font-variant-numeric: tabular-nums; }
.facturation-amount { font-size: 30px !important; font-variant-numeric: tabular-nums; }
/* Compactación real: no reservar superficie para gráficos sin actividad. */
.widget-card { min-height: 0 !important; }
.widget-card--dailyFacturation { min-height: 154px !important; }
.widget-card--criticalStock { min-height: 0 !important; }
.widget-card--sellerRanking, .widget-card--topCounterQueries { min-height: 190px !important; }
.widget-card--priceChangesToday { min-height: 142px !important; }
.widget-section { padding: 14px 16px !important; }
.widget-top { min-height: 40px !important; padding: 0 16px !important; }
.metric-tile { min-height: 58px !important; padding: 8px 11px !important; }
.metric-tile strong { font-size: 19px !important; }
.facturation-compact { min-height: 0 !important; }
.widget-card--dailyFacturation .facturation-chart-mini { display: none !important; }
.visual-row { margin-top: 12px !important; }
.chart-cell--donut { max-width: 180px; }
@media (max-width: 960px) { .widget-card--criticalStock { min-height: 260px; } }
  padding-bottom: 32px;
  font-family: inherit;
}

.hero {
  background: var(--bg-card);
  border-radius: var(--radius-lg);
  padding: 4px 16px;
  border: 1px solid var(--border-subtle);
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.hero h2 {
  margin: 0;
  font-size: 1.2rem;
  font-weight: 800;
  color: var(--text-primary);
  letter-spacing: -0.02em;
}

.hero p {
  margin: 0;
  font-size: 0.8rem;
  color: var(--text-secondary);
}

.widget-card {
  border-radius: var(--radius-lg);
  border: 1px solid var(--border-color);
  background: var(--bg-card);
  overflow: hidden;
  height: 100%;
}

.widget-top {
  padding: 8px 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid var(--border-color);
}

.widget-top h3 {
  margin: 0;
  font-size: 0.9rem;
  font-weight: 700;
  color: var(--text-primary);
}

/* Resize handle para widgets */
.widget-card {
  position: relative;
  transition: box-shadow 0.2s ease;
}

/* Widget con altura personalizada - contenido scrollable */
.widget-card[style*="height"] {
  display: flex;
  flex-direction: column;
}

.widget-card[style*="height"] .widget-section {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
}

/* Scrollbar estilizada para widgets */
.widget-section::-webkit-scrollbar {
  width: 6px;
}

.widget-section::-webkit-scrollbar-track {
  background: transparent;
}

.widget-section::-webkit-scrollbar-thumb {
  background: var(--border-color);
  border-radius: 3px;
}

.widget-section::-webkit-scrollbar-thumb:hover {
  background: var(--text-tertiary);
}

.widget-resize-handle {
  position: absolute;
  bottom: 4px;
  right: 4px;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: se-resize;
  color: var(--text-tertiary);
  opacity: 0.5;
  transition: opacity 0.2s ease, color 0.2s ease;
  z-index: 10;
  border-radius: 4px;
}

.widget-resize-handle:hover {
  opacity: 1;
  color: var(--accent-primary);
  background: rgba(59, 130, 246, 0.1);
}

.widget-card:hover .widget-resize-handle {
  opacity: 0.7;
}

.widget-resizing {
  user-select: none;
  box-shadow: 0 0 0 2px var(--accent-primary) !important;
}

/* Drag & Drop estilos */
.widget-col {
  transition: transform 0.2s ease, opacity 0.2s ease;
}

.widget-col.widget-dragging {
  opacity: 0.4;
  cursor: move;
}

.widget-col.widget-drop-target {
  position: relative;
}

.widget-col.widget-drop-target::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border: 2px dashed var(--accent-primary);
  border-radius: var(--radius-lg);
  background: rgba(59, 130, 246, 0.05);
  pointer-events: none;
  z-index: 5;
}

.widget-drag-handle {
  cursor: grab;
}

.widget-drag-handle:active {
  cursor: grabbing;
}

.widget-drag-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  margin-right: 8px;
  color: var(--text-tertiary);
  cursor: grab;
  opacity: 0.5;
  transition: opacity 0.2s ease, color 0.2s ease;
}

.widget-drag-icon:hover {
  opacity: 1;
  color: var(--accent-primary);
}

.widget-card:hover .widget-drag-icon {
  opacity: 0.7;
}

.metrics-row {
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
}

.metric-tile {
  background: var(--surface-muted);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-md);
  padding: 8px 12px;
  flex: 1;
}

.metric-tile span {
  font-size: 0.75rem;
  color: var(--text-tertiary);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-weight: 600;
}

.metric-tile strong {
  display: block;
  font-size: 1.2rem;
  color: var(--text-primary);
  margin-top: 2px;
}

.metric-tile.danger {
  background: rgba(153, 27, 27, 0.08);
  border-color: rgba(153, 27, 27, 0.2);
}

.metric-tile.danger span {
  color: #991B1B;
}

.metric-tile.danger strong {
  color: #7f1d1d;
}

.metric-tile.success {
  background: rgba(22, 101, 52, 0.08);
  border-color: rgba(22, 101, 52, 0.2);
}

.metric-tile.success span {
  color: #166534;
}

.metric-tile.success strong {
  color: #14532d;
}

.metric-tile.warning {
  background: rgba(133, 77, 14, 0.08);
  border-color: rgba(133, 77, 14, 0.2);
}

.metric-tile.warning span {
  color: #854D0E;
}

.metric-tile.warning strong {
  color: #713f12;
}

.metric-tile.info {
  background: rgba(30, 58, 138, 0.08);
  border-color: rgba(30, 58, 138, 0.2);
}

.metric-tile.info span {
  color: #1E3A8A;
}

.metric-tile.info strong {
  color: #1e3a8a;
}

.visual-row {
  display: flex;
  gap: 24px;
  align-items: flex-start;
}

.chart-cell {
  flex: 1;
}

.chart-cell--donut {
  max-width: 200px;
}

.bars-wrap {
  flex: 2;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.critical-row {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.critical-info {
  display: flex;
  justify-content: space-between;
  font-size: 0.8rem;
}

.critical-code {
  font-weight: 700;
  color: var(--text-primary);
}

.critical-stock {
  color: var(--text-tertiary);
}

.critical-bar-track {
  height: 6px;
  background: var(--border-color);
  border-radius: 3px;
  overflow: hidden;
}

.critical-bar-fill {
  height: 100%;
  background: linear-gradient(90deg, #991B1B, #B91C1C);
  border-radius: 3px;
}

.bars {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.bar-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.bar-label {
  width: 100px;
  font-size: 0.8rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: var(--text-secondary);
}

.bar-track {
  flex: 1;
  height: 8px;
  background: var(--border-color);
  border-radius: 4px;
}

.bar-fill {
  height: 100%;
  background: var(--accent-primary);
  border-radius: 4px;
}

.bar-value {
  width: 40px;
  text-align: right;
  font-size: 0.8rem;
  font-weight: 700;
}

/* Widget de Facturación - Ultra compacto */
.widget-section--facturation {
  padding: 0;
}

.facturation-compact {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.facturation-data {
  flex: 1;
  min-width: 0;
}

.facturation-header {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 2px;
}

.facturation-header span {
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

.facturation-row {
  display: flex;
  align-items: baseline;
  gap: 10px;
  flex-wrap: wrap;
}

.facturation-amount {
  font-size: 1.5rem;
  font-weight: 700;
  color: #0f172a;
  letter-spacing: -0.01em;
  line-height: 1.1;
}

.facturation-meta {
  font-size: 11px;
  color: #64748b;
  font-weight: 500;
}

.facturation-link {
  flex-shrink: 0;
}

.facturation-chart-mini {
  margin-top: 8px;
  height: 50px;
}

/* Modo compacto (3 columnas) - ajustes especiales */
@media (min-width: 960px) {
  .widget-card--dailyFacturation .facturation-compact {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
  
  .widget-card--dailyFacturation .facturation-row {
    flex-direction: column;
    gap: 2px;
  }
  
  .widget-card--dailyFacturation .facturation-amount {
    font-size: 1.3rem;
  }
  
  .widget-card--dailyFacturation .facturation-meta {
    font-size: 10px;
  }
}

/* Modo tiny (2 columnas = 16.67%) - ultra compacto */
@media (min-width: 960px) {
  [data-widget-id].col-md-2 .widget-card--dailyFacturation .facturation-compact {
    flex-direction: row;
    flex-wrap: wrap;
    gap: 4px;
  }
  
  [data-widget-id].col-md-2 .widget-card--dailyFacturation .facturation-header {
    width: 100%;
    margin-bottom: 0;
  }
  
  [data-widget-id].col-md-2 .widget-card--dailyFacturation .facturation-header span {
    font-size: 9px;
  }
  
  [data-widget-id].col-md-2 .widget-card--dailyFacturation .facturation-amount {
    font-size: 1.1rem;
    width: 100%;
  }
  
  [data-widget-id].col-md-2 .widget-card--dailyFacturation .facturation-meta {
    display: none; /* Ocultar metadata en modo tiny */
  }
  
  [data-widget-id].col-md-2 .widget-card--dailyFacturation .facturation-link {
    position: absolute;
    top: 8px;
    right: 8px;
    width: 20px;
    height: 20px;
  }
  
  [data-widget-id].col-md-2 .widget-card--dailyFacturation .facturation-chart-mini {
    display: none; /* Ocultar gráfico en modo tiny */
  }
}

/* Colores de widget según paleta ERP */
.widget-card--sellerRanking .widget-top {
  border-bottom-color: rgba(37, 99, 235, 0.3);
}

.widget-title--sellerRanking {
  color: #2563EB;
}

.widget-card--sellerRanking .bar-fill {
  background: linear-gradient(90deg, #3b82f6 0%, #2563EB 100%);
}

.widget-card--dailyFacturation .widget-top {
  border-bottom-color: rgba(22, 101, 52, 0.3);
}

.widget-title--dailyFacturation {
  color: #166534;
}

.widget-card--topCounterQueries .widget-top {
  border-bottom-color: rgba(30, 58, 138, 0.3);
}

.widget-title--topCounterQueries {
  color: #1E3A8A;
}

.widget-card--topCounterQueries .bar-fill {
  background: linear-gradient(90deg, #3b82f6 0%, #1E3A8A 100%);
}

.widget-card--priceChangesToday .widget-top {
  border-bottom-color: rgba(133, 77, 14, 0.3);
}

.widget-title--priceChangesToday {
  color: #854D0E;
}

.widget-card--criticalStock .widget-top {
  border-bottom-color: rgba(153, 27, 27, 0.3);
  background: rgba(153, 27, 27, 0.04);
}

.widget-title--criticalStock {
  color: #991B1B;
}

.widget-card--criticalStock .critical-bar-fill {
  background: linear-gradient(90deg, #DC2626 0%, #991B1B 100%);
}

.legend {
  margin-top: 10px;
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: center;
  font-size: 12px;
  color: #3a4c6a;
}

.dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 4px;
}

.dot.up {
  background: #166534;
}

.dot.down {
  background: #991B1B;
}

.dot.zero {
  background: #DC2626;
}

.dot.below {
  background: #B45309;
}

.line-wrap {
  flex: 1;
  min-width: 230px;
}

.line-chart {
  width: 100%;
  height: 90px;
  background: #f8fbff;
  border: 1px solid #dce8ff;
  border-radius: 10px;
}

.line-caption {
  margin-top: 6px;
  font-size: 12px;
  color: #5f7396;
}

.bars,
.bars-wrap {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.bar-row {
  display: grid;
  grid-template-columns: 92px 1fr 42px;
  gap: 8px;
  align-items: center;
}

.bar-label {
  font-size: 12px;
  color: #324766;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.bar-track {
  height: 9px;
  background: #dce6f7;
  border-radius: 999px;
  overflow: hidden;
}

.bar-fill {
  height: 100%;
  background: linear-gradient(90deg, #3b82f6 0%, #1d4ed8 100%);
  border-radius: 999px;
}

.bar-value {
  text-align: right;
  font-size: 12px;
  color: var(--ink);
}

.widget-section--queries .empty-cell {
  margin-top: 8px;
  padding: 10px 12px;
  border: 1px dashed #c6d5ef;
  border-radius: 10px;
  background: #f8fbff;
}

.empty-cell {
  color: #6a7d9c;
  font-size: 13px;
  text-align: center;
  padding: 6px 0;
}

.critical-row {
  display: grid;
  grid-template-columns: 70px 1fr auto;
  align-items: center;
  gap: 10px;
  padding: 5px 0;
}

.critical-code {
  font-size: 12px;
  color: #354968;
  white-space: nowrap;
}

.critical-stock {
  font-size: 11px;
  color: #5f7396;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.critical-miss {
  font-size: 12px;
  color: var(--ink);
  font-weight: 700;
  white-space: nowrap;
}

html[data-theme="dark"] .home {
  --ink: #e5edff;
  --muted: #a8bbda;
}

html[data-theme="dark"] .hero {
  background: linear-gradient(140deg, rgba(24, 37, 66, 0.92) 0%, rgba(19, 31, 58, 0.9) 52%, rgba(16, 28, 52, 0.9) 100%);
  border-color: rgba(148, 163, 184, 0.24);
  box-shadow: 0 14px 32px rgba(2, 6, 23, 0.42);
}

html[data-theme="dark"] .hero h2 {
  color: #e5edff;
}

html[data-theme="dark"] .hero p {
  color: #9fb3d5;
}

html[data-theme="dark"] .hero-refresh-btn {
  color: #dbeafe !important;
  background: rgba(30, 41, 59, 0.76) !important;
  border-color: rgba(148, 163, 184, 0.34);
}

html[data-theme="dark"] .hero-widget-btn {
  box-shadow: 0 10px 20px rgba(30, 64, 175, 0.34);
}

html[data-theme="dark"] .widget-menu-card {
  background: #15223f !important;
  border-color: rgba(148, 163, 184, 0.3);
  color: #dbeafe;
}

html[data-theme="dark"] .widget-card {
  background: linear-gradient(180deg, #1b2744 0%, #17223d 100%);
  border-color: rgba(148, 163, 184, 0.22);
  box-shadow: 0 12px 24px rgba(2, 6, 23, 0.44);
}

html[data-theme="dark"] .widget-card .v-card__text,
html[data-theme="dark"] .widget-card .v-card__text * {
  color: #a8bbda;
}

html[data-theme="dark"] .widget-top h3,
html[data-theme="dark"] .bar-value,
html[data-theme="dark"] .critical-miss,
html[data-theme="dark"] .metric-tile strong,
html[data-theme="dark"] .donut-hole {
  color: #e5edff;
}

html[data-theme="dark"] .widget-icon-btn {
  color: #9fb3d5 !important;
}

html[data-theme="dark"] .metric-tile {
  background: #1e2f54;
  border-color: rgba(148, 163, 184, 0.34);
}

html[data-theme="dark"] .metric-tile span,
html[data-theme="dark"] .caption,
html[data-theme="dark"] .line-caption,
html[data-theme="dark"] .legend,
html[data-theme="dark"] .bar-label,
html[data-theme="dark"] .critical-code,
html[data-theme="dark"] .critical-stock,
html[data-theme="dark"] .empty-cell {
  color: #a8bbda;
}

html[data-theme="dark"] .metric-tile.danger {
  background: #3a1e33;
  border-color: rgba(244, 114, 182, 0.32);
}

html[data-theme="dark"] .donut-hole {
  background: #111b32;
}

html[data-theme="dark"] .line-chart {
  background: #111b32;
  border-color: rgba(148, 163, 184, 0.34);
}

html[data-theme="dark"] .line-chart line {
  stroke: #32486f;
}

html[data-theme="dark"] .bar-track {
  background: #2a3b5c;
}

html[data-theme="dark"] .widget-section--queries .empty-cell {
  border-color: rgba(148, 163, 184, 0.36);
  background: #111b32;
}

html[data-theme="dark"] .widget-menu-card .v-card__title,
html[data-theme="dark"] .widget-menu-card .v-label,
html[data-theme="dark"] .widget-menu-card .v-icon {
  color: #dbeafe !important;
}

html[data-theme="dark"] .dot.up {
  background: #34d399;
}

html[data-theme="dark"] .dot.down {
  background: #fb7185;
}

html[data-theme="dark"] .dot.zero {
  background: #ef4444;
}

html[data-theme="dark"] .dot.below {
  background: #fbbf24;
}

@media (max-width: 960px) {
  .hero {
    flex-direction: column;
  }

  .hero h2 {
    font-size: 28px;
  }
}
</style>
